import {
	KristaConfidence,
	KristaGenerateResponse,
	KristaNarrativeStatus,
	KristaProvenance,
} from '../interfaces';

/**
 * Fields every finished Krista response can show above its narrative.
 * Driver reports, summaries, slides, and QBRs share this shape.
 */
export interface InsightResponseMeta {
	status: KristaNarrativeStatus;
	confidence?: KristaConfidence;
	caveat?: string;
	model?: string;
	promptVersion?: string;
	kbVersion?: string;
	generatedAt?: string;
	calculationId?: string;
	targetId?: string | null;
}

const Confidences = new Set<KristaConfidence>(['low', 'medium', 'high']);

/**
 * Reads the shared response header. Accepts the app model and a stored
 * Krista payload whose provenance is still snake_case.
 */
export function insightResponseMeta(
	response: KristaGenerateResponse | null | undefined,
): InsightResponseMeta | null {
	if (!response || typeof response !== 'object' || !response.status) {
		return null;
	}

	const confidence = response.confidence;
	const caveat = stringField(response.caveat);
	const provenance = provenanceOf(response);

	return {
		status: response.status,
		...(isConfidence(confidence) ? { confidence } : {}),
		...(caveat ? { caveat } : {}),
		...provenance,
	};
}

function provenanceOf(
	response: KristaGenerateResponse,
): Omit<InsightResponseMeta, 'status' | 'confidence' | 'caveat'> {
	const stored = response.provenance as
		| (Partial<KristaProvenance> & Record<string, unknown>)
		| undefined;
	if (!stored || typeof stored !== 'object') return {};

	const model = stringField(stored.model);
	const promptVersion = stringField(
		stored.promptVersion ?? stored['prompt_version'],
	);
	const kbVersion = stringField(stored.kbVersion ?? stored['kb_version']);
	const generatedAt = stringField(
		stored.generatedAt ?? stored['generated_at'],
	);
	const calculationId = stringField(
		stored.calculationId ?? stored['calculation_id'],
	);
	const target = stored.targetId ?? stored['target_id'];

	return {
		...(model ? { model } : {}),
		...(promptVersion ? { promptVersion } : {}),
		...(kbVersion ? { kbVersion } : {}),
		...(generatedAt ? { generatedAt } : {}),
		...(calculationId ? { calculationId } : {}),
		...(typeof target === 'string' ? { targetId: target } : {}),
	};
}

function isConfidence(value: unknown): value is KristaConfidence {
	return (
		typeof value === 'string' && Confidences.has(value as KristaConfidence)
	);
}

function stringField(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim().length > 0
		? value.trim()
		: undefined;
}
